/**
 * SessionTimeout — tracks user activity and shows a warning after 25 minutes of
 * inactivity with a 5-minute countdown before logout (30 minutes total).
 *
 * NP-484: the decision is made from the WALL CLOCK, not from timer callbacks.
 * Timers stop while an iOS device is locked and are throttled in a background
 * tab, so a phone left locked for 30 minutes came back still signed in. The
 * timers below are now only an optimisation for the foreground case; the source
 * of truth is a persisted `lastActivityAt` re-read every time the app becomes
 * visible again. See src/lib/sessionActivity.ts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';
import { SESSION_EXPIRED_PARAM, SESSION_EXPIRY_INACTIVITY } from '@/lib/sessionExpiry';
import {
  IDLE_TIMEOUT_MS,
  WARNING_DURATION_MS,
  clearLastActivityAt,
  evaluateIdleState,
  readLastActivityAt,
  writeLastActivityAt,
} from '@/lib/sessionActivity';

/**
 * NP-484: activity fires on mousemove/scroll, so the persisted stamp is written
 * at most this often. Minute-scale decisions do not need per-pixel accuracy, and
 * a localStorage write per mousemove event would be wasteful.
 */
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

export function SessionTimeout() {
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Track current location in a ref so handleLogout always captures the
  // *current* path, not the stale one from when the callback was created.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Use a ref to track warning state inside effects without causing re-registration
  const showWarningRef = useRef(false);
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  const clearTimers = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    idleTimer.current = null;
    countdownInterval.current = null;
  }, []);

  /**
   * NP-456: `expired` distinguishes the countdown running out from the user
   * pressing "Sign out now". Both end the session, but only the former is a
   * session EXPIRY — telling someone who just clicked sign-out that their
   * session "expired due to inactivity" would be wrong.
   */
  const handleLogout = useCallback(async ({ expired = false }: { expired?: boolean } = {}) => {
    clearTimers();
    setShowWarning(false);
    // NP-484: drop the stamp so the next sign-in starts from a clean window
    // instead of inheriting the previous session's idle time.
    clearLastActivityAt();
    // Use the ref to read the *current* location, not the stale closure value
    const loc = locationRef.current;
    const currentPath = loc.pathname + loc.search;

    // Review (NP-484): a rejected logout() used to bubble out of this handler, so
    // the navigation below never ran — timers already cleared, user still on the
    // page, no idle protection left, plus an unhandled rejection. Redirecting
    // anyway is the safer failure mode: the auth guard re-evaluates on /login, and
    // a user who is somehow still authenticated can sign out by hand.
    try {
      await logout();
    } catch {
      /* fall through to the redirect */
    }

    const params = new URLSearchParams();
    if (currentPath && currentPath !== '/' && currentPath !== '/login') {
      params.set('returnUrl', currentPath);
    }
    if (expired) {
      params.set(SESSION_EXPIRED_PARAM, SESSION_EXPIRY_INACTIVITY);
    }
    const search = params.toString();

    // NP-456: the reason travels as a QUERY PARAM as well as route state.
    // Route state alone is fragile — it does not survive a full document load,
    // and NP-442 (PR #101) deliberately reloads once on a stale lazy chunk, which
    // is exactly the kind of navigation that would silently drop it. The param
    // survives; the state is kept so in-app navigation needs no URL parsing.
    navigate(`/login${search ? `?${search}` : ''}`, {
      replace: true,
      ...(expired && { state: { sessionExpired: SESSION_EXPIRY_INACTIVITY } }),
    });
  }, [clearTimers, logout, navigate]);

  /**
   * NP-484: `secondsRemaining` lets a resume pick the countdown up where the wall
   * clock says it actually is, rather than restarting a full five minutes — a
   * phone unlocked at minute 27 must show ~3 minutes, not 5.
   */
  const startCountdown = useCallback((secondsRemaining?: number) => {
    const totalSeconds = secondsRemaining ?? Math.floor(WARNING_DURATION_MS / 1000);
    setSecondsLeft(totalSeconds);
    setShowWarning(true);

    const deadline = Date.now() + totalSeconds * 1000;
    countdownInterval.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        // Countdown ran out: this IS a session expiry.
        handleLogout({ expired: true });
      }
    }, 1000);
  }, [handleLogout]);

  /**
   * NP-484: the single decision point. Reads the persisted stamp, asks the pure
   * evaluator where the session stands, and reconciles the UI and timers with the
   * answer. Called on mount, on every resume, and after a suspend — anywhere the
   * timers cannot be trusted.
   */
  const syncWithWallClock = useCallback(() => {
    clearTimers();
    const state = evaluateIdleState(readLastActivityAt(), Date.now());

    if (state.phase === 'expired') {
      handleLogout({ expired: true });
      return;
    }

    if (state.phase === 'warning') {
      startCountdown(state.secondsLeft);
      return;
    }

    setShowWarning(false);
    idleTimer.current = setTimeout(startCountdown, state.msUntilWarning);
  }, [clearTimers, handleLogout, startCountdown]);

  const lastWriteRef = useRef(0);

  const resetIdleTimer = useCallback(() => {
    // If warning is already showing, don't reset — user must click "Stay signed in"
    if (showWarningRef.current) return;

    // NP-484: the persisted stamp is what survives a suspend, so activity has to
    // move it and not just the timer. Throttled, since mousemove fires constantly.
    const now = Date.now();
    if (now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
      lastWriteRef.current = now;
      writeLastActivityAt(now);
    }

    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(startCountdown, IDLE_TIMEOUT_MS);
  }, [startCountdown]);

  const handleStaySignedIn = useCallback(() => {
    clearTimers();
    setShowWarning(false);
    // NP-484: staying signed in has to move the persisted stamp too, or the very
    // next resume would read the old one and expire the session anyway.
    const now = Date.now();
    lastWriteRef.current = now;
    writeLastActivityAt(now);
    // Restart the idle timer
    idleTimer.current = setTimeout(startCountdown, IDLE_TIMEOUT_MS);
  }, [clearTimers, startCountdown]);

  // Attach activity listeners (mount-only for the event registration)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handler = () => resetIdleTimer();
    ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, handler, { passive: true }));

    // NP-484: seed the stamp when there isn't one, so a session that began before
    // this shipped (or a fresh login) starts its window now rather than reading as
    // "never active" forever.
    if (readLastActivityAt() === null) {
      const now = Date.now();
      lastWriteRef.current = now;
      writeLastActivityAt(now);
    }

    /**
     * NP-484: the fix. Every one of these fires on the return path from a locked
     * or backgrounded device, and each is needed:
     *  - visibilitychange — tab foregrounded, the common unlock case
     *  - focus            — window regains focus without a visibility change
     *  - pageshow         — restore from the back/forward cache, which iOS Safari
     *                       uses aggressively and which does NOT re-run mount
     */
    // visibilitychange fires on the way OUT as well as the way in, so this one
    // needs the guard — backgrounding must not evaluate.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') return;
      syncWithWallClock();
    };

    // focus and pageshow only ever fire on the way back (backgrounding produces
    // blur and pagehide instead), so they evaluate unconditionally. Deliberately
    // NOT gated on visibilityState: during a back/forward-cache restore that flag
    // is not guaranteed to have flipped to 'visible' yet, and gating on it would
    // make the whole mechanism hostage to one signal's ordering.
    const handleReturn = () => syncWithWallClock();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleReturn);
    window.addEventListener('pageshow', handleReturn);

    // Mount also covers the case where the browser evicted the tab and reloaded
    // the document — the old in-memory timer is gone, but the stamp is not.
    syncWithWallClock();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, handler));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleReturn);
      window.removeEventListener('pageshow', handleReturn);
      clearTimers();
    };
  }, [isAuthenticated, resetIdleTimer, syncWithWallClock, clearTimers]);

  if (!isAuthenticated) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <Dialog
      open={showWarning}
      onOpenChange={(open) => {
        if (!open) handleStaySignedIn();
      }}
    >
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full border border-border bg-muted/40">
            <Clock className="size-6 text-foreground" strokeWidth={1.5} />
          </div>
          <DialogTitle className="text-center text-xl tracking-tight">
            Your session will expire soon
          </DialogTitle>
        </DialogHeader>
        <p className="text-center text-sm text-muted-foreground leading-relaxed">
          Your session will expire in{' '}
          <span className="font-semibold text-foreground">{timeDisplay}</span>.
        </p>
        <DialogFooter className="mt-2 flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button onClick={handleStaySignedIn} className="w-full">
            Stay signed in
          </Button>
          <Button variant="ghost" onClick={() => handleLogout()} className="w-full">
            Sign out now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
